import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import AddCommentIcon from '@mui/icons-material/AddComment';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { formatNumber } from '../utils/scoreColors';

const DEFAULT_BUDGET_ITEMS = [
  { categorie: 'ASSURANCES', categorieKey: 'ASSURANCES', item: 'assurance_multirisques', frequence: 'annuel', nature: 'recurrente' },
  { categorie: 'ASSURANCES', categorieKey: 'ASSURANCES', item: 'assurance_responsabilite', frequence: 'annuel', nature: 'recurrente' },
  { categorie: 'ASSURANCES', categorieKey: 'ASSURANCES', item: 'assurance_renouvellement', frequence: 'annuel', nature: 'recurrente' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'entretien_toiture', frequence: 'selon_besoin', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'entretien_cloches', frequence: 'annuel', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'entretien_facade', frequence: 'selon_besoin', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'entretien_chauffage', frequence: 'selon_besoin', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'entretien_ventilation', frequence: 'selon_besoin', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'entretien_ascenseur', frequence: 'annuel', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'entretien_electricite', frequence: 'annuel', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'entretien_plomberie', frequence: 'selon_besoin', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'entretien_alarme', frequence: 'selon_besoin', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'securite', frequence: 'selon_besoin', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'entretien_batiment', frequence: 'selon_besoin', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'jardin', frequence: 'selon_besoin', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'panneaux_solaires', frequence: 'annuel', nature: 'maintenance' },
  { categorie: 'ENTRETIEN_MAINTENANCE', categorieKey: 'ENTRETIEN_MAINTENANCE', item: 'reseau_informatique', frequence: 'selon_besoin', nature: 'maintenance' },
  { categorie: 'ENERGIE', categorieKey: 'ENERGIE', item: 'chauffage_annuel', frequence: 'annuel', nature: 'energie' },
  { categorie: 'ENERGIE', categorieKey: 'ENERGIE', item: 'electricite', frequence: 'annuel', nature: 'energie' },
  { categorie: 'ENERGIE', categorieKey: 'ENERGIE', item: 'eau', frequence: 'mensuel', nature: 'energie' },
  { categorie: 'ENERGIE', categorieKey: 'ENERGIE', item: 'electricite_eau_sanitaire', frequence: 'selon_besoin', nature: 'energie' },
  { categorie: 'NETTOYAGE_HYGIENE', categorieKey: 'NETTOYAGE_HYGIENE', item: 'nettoyage_regulier', frequence: 'hebdomadaire_mensuel', nature: 'nettoyage' },
  { categorie: 'NETTOYAGE_HYGIENE', categorieKey: 'NETTOYAGE_HYGIENE', item: 'nettoyage_specialise', frequence: 'trimestriel', nature: 'nettoyage' },
  { categorie: 'PERSONNEL_GESTION', categorieKey: 'PERSONNEL_GESTION', item: 'salaire_gardien', frequence: 'mensuel', nature: 'personnel' },
  { categorie: 'PERSONNEL_GESTION', categorieKey: 'PERSONNEL_GESTION', item: 'salaire_maintenance', frequence: 'mensuel', nature: 'personnel' },
  { categorie: 'PERSONNEL_GESTION', categorieKey: 'PERSONNEL_GESTION', item: 'honoraires_syndic', frequence: 'mensuel', nature: 'gestion' },
  { categorie: 'REVENUS', categorieKey: 'REVENUS', item: 'revenu_location', frequence: 'mensuel', nature: 'recurrente' },
  { categorie: 'REVENUS', categorieKey: 'REVENUS', item: 'vente_energie_renouvelable', frequence: 'mensuel', nature: 'recurrente' },
  { categorie: 'REVENUS', categorieKey: 'REVENUS', item: 'dons_legs', frequence: 'selon_besoin', nature: 'recurrente' },
  { categorie: 'REVENUS', categorieKey: 'REVENUS', item: 'divers', frequence: 'selon_besoin', nature: 'recurrente' }
];

const keyOf = (cat, item) => `${cat}|${item}`;

function OperatingBudget({ budgets, setBudgets, isDataLoaded = false, buildings = [] }) {
  const { t } = useTranslation();
  const hasMerged = useRef(false);
  const [commentDialog, setCommentDialog] = useState({ open: false, lineKey: null, value: '' });
  const [toggleDialog, setToggleDialog] = useState({ open: false, type: null, groupKey: null });

  useEffect(() => {
    if (hasMerged.current) return;
    if (!isDataLoaded) return;

    // Indexer les lignes existantes par item_budget + batiment_id
    const existingByKey = new Map();
    budgets.forEach(b => {
      const k = keyOf(b.categorie_principale || b.categorie, (b.item_budget || b.item || '').toLowerCase());
      if (!existingByKey.has(k)) existingByKey.set(k, []);
      existingByKey.get(k).push(b);
    });

    const mergedBudgets = [];

    DEFAULT_BUDGET_ITEMS.forEach(defaultItem => {
      const k = keyOf(defaultItem.categorie, defaultItem.item.toLowerCase());
      const existingLines = existingByKey.get(k) || [];

      const subLines = existingLines.filter(l => l.batiment_id);
      const siteLine = existingLines.find(l => !l.batiment_id);

      if (subLines.length > 0) {
        // Mode déplié déjà persisté : on garde les sous-lignes uniquement
        subLines.forEach(sl => {
          mergedBudgets.push({
            id: sl.id,
            categorieKey: defaultItem.categorieKey,
            categorie: defaultItem.categorie,
            categorie_principale: defaultItem.categorie,
            item: defaultItem.item,
            item_budget: defaultItem.item,
            frequence: defaultItem.frequence,
            nature: defaultItem.nature,
            batiment_id: sl.batiment_id,
            montant: sl.montant || 0,
            commentaire: sl.commentaire || ''
          });
        });
      } else if (siteLine) {
        // Mode global persisté
        mergedBudgets.push({
          id: siteLine.id,
          categorieKey: defaultItem.categorieKey,
          categorie: defaultItem.categorie,
          categorie_principale: defaultItem.categorie,
          item: defaultItem.item,
          item_budget: defaultItem.item,
          frequence: defaultItem.frequence,
          nature: defaultItem.nature,
          batiment_id: null,
          montant: siteLine.montant || 0,
          commentaire: siteLine.commentaire || ''
        });
      } else {
        // Nouvel item, ligne site vide
        mergedBudgets.push({
          categorieKey: defaultItem.categorieKey,
          categorie: defaultItem.categorie,
          categorie_principale: defaultItem.categorie,
          item: defaultItem.item,
          item_budget: defaultItem.item,
          frequence: defaultItem.frequence,
          nature: defaultItem.nature,
          batiment_id: null,
          montant: 0,
          commentaire: ''
        });
      }
    });

    setBudgets(mergedBudgets);
    hasMerged.current = true;
  }, [budgets, isDataLoaded, setBudgets]);

  // Groupes par item_budget — détermine le mode (global/expanded) et le rendu
  const groups = useMemo(() => {
    const map = new Map();
    DEFAULT_BUDGET_ITEMS.forEach(def => {
      const k = keyOf(def.categorie, def.item);
      map.set(k, { def, siteIndex: null, subIndexes: [] });
    });
    budgets.forEach((b, i) => {
      const k = keyOf(b.categorie_principale || b.categorie, b.item_budget || b.item);
      if (!map.has(k)) {
        // Item inconnu (legacy / hors DEFAULT) : on l'ajoute en fin
        map.set(k, { def: { categorie: b.categorie_principale, categorieKey: b.categorieKey || b.categorie_principale, item: b.item_budget || b.item, frequence: b.frequence, nature: b.nature }, siteIndex: null, subIndexes: [] });
      }
      const g = map.get(k);
      if (b.batiment_id) g.subIndexes.push(i);
      else g.siteIndex = i;
    });
    return map;
  }, [budgets]);

  const updateMontant = (index, value) => {
    const newBudgets = [...budgets];
    newBudgets[index] = { ...newBudgets[index], montant: value };
    setBudgets(newBudgets);
  };

  const openCommentDialog = (index) => {
    setCommentDialog({ open: true, lineKey: index, value: budgets[index].commentaire || '' });
  };

  const saveComment = () => {
    const newBudgets = [...budgets];
    newBudgets[commentDialog.lineKey] = { ...newBudgets[commentDialog.lineKey], commentaire: commentDialog.value };
    setBudgets(newBudgets);
    setCommentDialog({ open: false, lineKey: null, value: '' });
  };

  const expandItem = (groupKey) => {
    if (!buildings || buildings.length === 0) return;
    const g = groups.get(groupKey);
    if (!g) return;
    const def = g.def;

    const newBudgets = [...budgets];
    // Supprimer la ligne site si présente (en récupérant son commentaire éventuel pour ne pas le perdre)
    let priorComment = '';
    if (g.siteIndex !== null) {
      priorComment = newBudgets[g.siteIndex].commentaire || '';
      newBudgets.splice(g.siteIndex, 1);
    }
    // Ajouter une sous-ligne par bâtiment
    buildings.forEach((b, idx) => {
      newBudgets.push({
        categorieKey: def.categorieKey || def.categorie,
        categorie: def.categorie,
        categorie_principale: def.categorie,
        item: def.item,
        item_budget: def.item,
        frequence: def.frequence,
        nature: def.nature,
        batiment_id: b.id,
        montant: 0,
        commentaire: idx === 0 ? priorComment : ''
      });
    });
    setBudgets(newBudgets);
  };

  const collapseItem = (groupKey) => {
    const g = groups.get(groupKey);
    if (!g) return;
    const def = g.def;

    const subBudgets = g.subIndexes.map(i => budgets[i]);
    const sumMontant = subBudgets.reduce((s, b) => s + parseFloat(b.montant || 0), 0);
    const concatComment = subBudgets
      .filter(b => b.commentaire && b.commentaire.trim())
      .map(b => {
        const bldg = buildings.find(bl => bl.id === b.batiment_id);
        const prefix = bldg ? `[${t('building')} ${bldg.numero_batiment}] ` : '';
        return `${prefix}${b.commentaire}`;
      })
      .join('\n');

    // Retirer toutes les sous-lignes, ajouter une ligne site avec la somme
    const idxSet = new Set(g.subIndexes);
    const newBudgets = budgets.filter((_, i) => !idxSet.has(i));
    newBudgets.push({
      categorieKey: def.categorieKey || def.categorie,
      categorie: def.categorie,
      categorie_principale: def.categorie,
      item: def.item,
      item_budget: def.item,
      frequence: def.frequence,
      nature: def.nature,
      batiment_id: null,
      montant: sumMontant,
      commentaire: concatComment
    });
    setBudgets(newBudgets);
  };

  const confirmToggle = () => {
    const { type, groupKey } = toggleDialog;
    if (type === 'expand') expandItem(groupKey);
    else if (type === 'collapse') collapseItem(groupKey);
    setToggleDialog({ open: false, type: null, groupKey: null });
  };

  // Totaux
  const expenseTotal = budgets
    .filter(b => (b.categorieKey || b.categorie || b.categorie_principale) !== 'REVENUS')
    .reduce((sum, b) => sum + parseFloat(b.montant || 0), 0);
  const revenueTotal = budgets
    .filter(b => (b.categorieKey || b.categorie || b.categorie_principale) === 'REVENUS')
    .reduce((sum, b) => sum + parseFloat(b.montant || 0), 0);

  const getTranslation = (key, value) => {
    if (!value) return '';
    const translated = t(key);
    return translated === key ? value : translated;
  };

  const renderGroupRows = (def) => {
    const k = keyOf(def.categorie, def.item);
    const g = groups.get(k);
    if (!g) return null;

    const isExpanded = g.subIndexes.length > 0;
    const categorieKey = (def.categorieKey || def.categorie || '').toLowerCase();
    const itemKey = (def.item || '').toLowerCase().replace(/ /g, '_');
    const natureKey = (def.nature || '').toLowerCase();

    const siteMontant = isExpanded
      ? g.subIndexes.reduce((sum, i) => sum + parseFloat(budgets[i].montant || 0), 0)
      : (g.siteIndex !== null ? budgets[g.siteIndex].montant : 0);

    const siteIdx = g.siteIndex;
    const siteHasComment = !isExpanded && siteIdx !== null && budgets[siteIdx].commentaire;

    return (
      <React.Fragment key={k}>
        {/* Ligne site (principale) */}
        <TableRow sx={isExpanded ? { backgroundColor: '#f3f7fb' } : {}}>
          <TableCell sx={{ width: 36 }}>
            {buildings.length > 0 && (
              <Tooltip title={isExpanded ? t('collapseHint') : t('expandHint')}>
                <IconButton size="small" onClick={() => setToggleDialog({ open: true, type: isExpanded ? 'collapse' : 'expand', groupKey: k })}>
                  {isExpanded ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
          <TableCell>{getTranslation(`budgetCategories.${categorieKey}`, def.categorie)}</TableCell>
          <TableCell>
            {getTranslation(`budgetItems.${itemKey}`, def.item)}
            {isExpanded && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({t('perBuilding')})
              </Typography>
            )}
          </TableCell>
          <TableCell>{getTranslation(`budgetNatures.${natureKey}`, def.nature)}</TableCell>
          <TableCell align="right">
            {isExpanded ? (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatNumber(siteMontant)}
              </Typography>
            ) : (
              <TextField
                type="number"
                value={siteIdx !== null ? budgets[siteIdx].montant : 0}
                onChange={(e) => updateMontant(siteIdx, e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                size="small"
                sx={{ width: 120 }}
              />
            )}
          </TableCell>
          <TableCell align="center">
            {!isExpanded && siteIdx !== null && (
              <Tooltip title={siteHasComment ? t('editComment') : t('addComment')}>
                <IconButton size="small" onClick={() => openCommentDialog(siteIdx)}>
                  {siteHasComment ? (
                    <CommentIcon color="primary" fontSize="small" />
                  ) : (
                    <AddCommentIcon fontSize="small" sx={{ color: '#bdbdbd' }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>

        {/* Sous-lignes par bâtiment */}
        {isExpanded && g.subIndexes.map((subIdx) => {
          const sub = budgets[subIdx];
          const bldg = buildings.find(b => b.id === sub.batiment_id);
          return (
            <TableRow key={`${k}-${sub.batiment_id}`} sx={{ backgroundColor: '#fafafa' }}>
              <TableCell />
              <TableCell />
              <TableCell sx={{ pl: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ApartmentIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {bldg ? `${t('building')} ${bldg.numero_batiment}${bldg.type_batiment ? ` — ${t(`buildingTypes.${bldg.type_batiment}`, bldg.type_batiment)}` : ''}` : `#${sub.batiment_id}`}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell />
              <TableCell align="right">
                <TextField
                  type="number"
                  value={sub.montant}
                  onChange={(e) => updateMontant(subIdx, e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  size="small"
                  sx={{ width: 120 }}
                />
              </TableCell>
              <TableCell align="center">
                <Tooltip title={sub.commentaire ? t('editComment') : t('addComment')}>
                  <IconButton size="small" onClick={() => openCommentDialog(subIdx)}>
                    {sub.commentaire ? (
                      <CommentIcon color="primary" fontSize="small" />
                    ) : (
                      <AddCommentIcon fontSize="small" sx={{ color: '#bdbdbd' }} />
                    )}
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </React.Fragment>
    );
  };

  const expenseDefs = DEFAULT_BUDGET_ITEMS.filter(d => d.categorie !== 'REVENUS');
  const revenueDefs = DEFAULT_BUDGET_ITEMS.filter(d => d.categorie === 'REVENUS');

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('operatingBudgetTab')}
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t('category')}</TableCell>
              <TableCell>{t('budgetItem')}</TableCell>
              <TableCell>{t('nature')}</TableCell>
              <TableCell align="right">{t('annualAmount')}</TableCell>
              <TableCell align="center">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenseDefs.map(def => renderGroupRows(def))}

            {/* Sous-total dépenses */}
            <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
              <TableCell colSpan={4} align="right">
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {t('totalExpenses')}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {formatNumber(expenseTotal)}
                </Typography>
              </TableCell>
              <TableCell />
            </TableRow>

            <TableRow>
              <TableCell colSpan={6} sx={{ padding: 0, height: 8, border: 'none' }} />
            </TableRow>

            {revenueDefs.map(def => renderGroupRows(def))}

            {/* Sous-total revenus */}
            <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
              <TableCell colSpan={4} align="right">
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {t('totalRevenues')}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {formatNumber(revenueTotal)}
                </Typography>
              </TableCell>
              <TableCell />
            </TableRow>

            {/* Total global : dépenses - revenus */}
            <TableRow sx={{ backgroundColor: '#fff3e0' }}>
              <TableCell colSpan={4} align="right">
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t('operatingBudgetTotal')}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formatNumber(expenseTotal - revenueTotal)}
                </Typography>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={commentDialog.open}
        onClose={() => setCommentDialog({ open: false, lineKey: null, value: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {commentDialog.lineKey !== null && budgets[commentDialog.lineKey]?.commentaire
            ? t('editComment')
            : t('addComment')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            value={commentDialog.value}
            onChange={(e) => setCommentDialog({ ...commentDialog, value: e.target.value })}
            placeholder={t('comment')}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialog({ open: false, lineKey: null, value: '' })}>
            {t('cancelComment')}
          </Button>
          <Button onClick={saveComment} variant="contained">
            {t('saveComment')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={toggleDialog.open}
        onClose={() => setToggleDialog({ open: false, type: null, groupKey: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {toggleDialog.type === 'collapse' ? t('collapseConfirmTitle') : t('expandConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {toggleDialog.type === 'collapse' ? t('collapseConfirmMessage') : t('expandConfirmMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleDialog({ open: false, type: null, groupKey: null })}>
            {t('cancel')}
          </Button>
          <Button onClick={confirmToggle} variant="contained">
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OperatingBudget;
